import { Box, Tooltip } from '@mui/material';
import { Tour } from '@styled-icons/material/Tour';
import ChainMapping from 'FeatureModules/ComponentChain/Components/ChainMapping';
import ChainTasksDetails from 'FeatureModules/ComponentChain/Components/ChainTasksDetails';
import CreateChainPlaceholder from 'FeatureModules/ComponentChain/Components/CreateChainPlaceholder';
import useComponentChainMapping from 'FeatureModules/ComponentChain/hooks/useComponentChainMapping';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import Loader from 'Components/Forms/Loader';
import useDialog from 'hooks/useDialog';
import usePageTitle from 'hooks/usePageTitle';
import { STEP_BY_STEP_LIST_HEIGHT } from 'FeatureModules/ComponentChain/utils/chainMapping';
import { InfoTooltip, ThemeButton } from 'DesignSystem';
import React, { FC } from 'react';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import {
  REVIEW_REQUEST_BUTTON_STATUS_CLIENT,
  REVIEW_REQUEST_BUTTON_STATUS_PARTNER,
} from 'FeatureModules/Request/utils/reviewRequestButtonStatus.utils';
import { ECustomerPlan } from 'FeatureModules/Auth/types/company.types';
import ErrorState from 'Components/ErrorState';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';

const ComponentChainMapping: FC = () => {
  usePageTitle('Component chain mapping');
  const {
    error,
    queryLoading,
    mutationLoading,
    componentChain,
    expandList,
    handleEditComponentChain,
    handleExpandList,
    handleClickOnboardingTour,
    handleCreateByCopyComponentClick,
    handleCreateNewChainClick,
    handleChainMappingAction,
  } = useComponentChainMapping();
  const { plan } = useCompanyPlanContext();
  const { openDialog } = useDialog();

  const isPartner = plan === ECustomerPlan.FREEMIUM;
  const request = componentChain?.partnerRequest;

  const diabledRequestButton = componentChain && componentChain.chainTasksDetails?.completed < 4;

  const handleClickFulfillRequestButton = () => {
    if (!componentChain?.partnerRequest) return;
    openDialog({
      type: 'FINISH_CHAIN_REQUEST',
      props: { requestId: componentChain?.partnerRequest.id, chain: componentChain },
    });
  };

  const handleClickReviewRequestButton = async () => {
    if (!componentChain?.partnerRequest) return;

    openDialog({
      type: 'REVIEW_CHAIN_REQUEST',
      props: { request: { ...componentChain?.partnerRequest, chain: componentChain } },
    });
  };

  if (queryLoading || !componentChain) {
    return <Loader />;
  }
  if (error || !componentChain) {
    return <ErrorState />;
  }

  return (
    <PageContainer>
      <Box display="flex" alignItems="end">
        <Box width="100%">
          <PageTitle
            title="Component chain mapping"
            titleExtension={componentChain.title}
            enableAutoGoBackHistoryButton
            goBackLabel="All component chains"
            goBackUrl="/component-chains"
            goBackExtension={
              request ? (
                <>
                  {isPartner ? (
                    <Tooltip
                      title={
                        REVIEW_REQUEST_BUTTON_STATUS_PARTNER[request.requestStatus].tooltip ||
                        `You need to complete at least 4/${componentChain.chainTasksDetails?.total} steps to confirm chain as completed`
                      }
                    >
                      <div>
                        <ThemeButton
                          startIcon={<Icon name="chain-request-fulfilled" />}
                          color="YELLOW_40"
                          onClick={handleClickFulfillRequestButton}
                          disabled={
                            REVIEW_REQUEST_BUTTON_STATUS_PARTNER[request.requestStatus].disabled ||
                            diabledRequestButton
                          }
                        >
                          Confirm chain mapping request as finished
                        </ThemeButton>
                      </div>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title={REVIEW_REQUEST_BUTTON_STATUS_CLIENT[request.requestStatus].tooltip}
                    >
                      <div>
                        <ThemeButton
                          startIcon={<Icon name="chain-request-fulfilled" />}
                          color="YELLOW_40"
                          onClick={handleClickReviewRequestButton}
                          disabled={
                            REVIEW_REQUEST_BUTTON_STATUS_CLIENT[request.requestStatus].disabled
                          }
                        >
                          Review chain mapping request status
                        </ThemeButton>
                      </div>
                    </Tooltip>
                  )}
                </>
              ) : undefined
            }
            TitleAdornment={
              isPartner ? (
                <></>
              ) : (
                <Box ml={1}>
                  <InfoTooltip
                    size={EAvailableSizes.LARGE}
                    text="Component chains are the sub-supply chains of your product supply chains. They describe the material flow of your components (ingredients, packaging or something else that makes up your end product). Using the component chains will help you to have better clarity and makes it easier to re-use your component chains across your different products' supply chains."
                  />
                </Box>
              )
            }
          />
        </Box>
        <ThemeButton
          color="WHITE"
          data-testid="restart-tour-button"
          size="small"
          onClick={handleClickOnboardingTour}
          startIcon={<Tour size={16} color="black" />}
        >
          Restart guided tour
        </ThemeButton>
      </Box>
      <Box position="relative" mt={2}>
        <ChainTasksDetails
          expanded={expandList}
          height={STEP_BY_STEP_LIST_HEIGHT}
          chainTasksDetails={componentChain.chainTasksDetails}
          title={componentChain.title}
          image={componentChain.image}
          onEditChain={handleEditComponentChain}
          onExpand={handleExpandList}
        />
      </Box>
      {mutationLoading && <Loader overlayOpacity />}
      <Box height="calc(100% - 180px)" mt={`${STEP_BY_STEP_LIST_HEIGHT + 35}px`} overflow="hidden">
        {!componentChain.mappingInitialised ? (
          <CreateChainPlaceholder
            isCopyButtonVisible={false} // Disabled until the proper duplication feature is implemented in the backend
            startFromScratchButtonLabel="Start empty chain"
            onCopyChainClick={handleCreateByCopyComponentClick}
            onStartFromSketchClick={handleCreateNewChainClick}
          />
        ) : (
          <ChainMapping
            viewMode={isPartner ? 'partner-default' : 'default'}
            chainMapping={componentChain}
            onActionClick={handleChainMappingAction}
          />
        )}
      </Box>
    </PageContainer>
  );
};

export default ComponentChainMapping;
